import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const VenueSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    name: "",
    location: "",
    city: "",
    district: "",
    address: "",
    phone: "",
    description: "",
    capacity: 14,
    pricePerHour: 500,
    format: "7x7",
    surfaceType: "artificial_grass",
    availableHours: "08:00-23:00",
    hasChangingRoom: true,
    hasShower: true,
    hasParking: true,
    images: [],
    amenities: []
  });

  useEffect(() => {
    if (venueId) {
      fetchVenue();
    }
  }, [venueId]);

  const fetchVenue = async () => {
    try {
      const response = await axios.get(`${API}/venues/${venueId}`);
      setVenue(response.data);
      setSettings({
        name: response.data.name || "",
        location: response.data.location || "",
        city: response.data.city || "",
        district: response.data.district || "",
        address: response.data.address || "",
        phone: response.data.phone || "",
        description: response.data.description || "",
        capacity: response.data.capacity || 14,
        pricePerHour: response.data.pricePerHour || 500,
        format: response.data.format || "7x7",
        surfaceType: response.data.surfaceType || "artificial_grass",
        availableHours: response.data.availableHours || "08:00-23:00",
        hasChangingRoom: response.data.hasChangingRoom !== false,
        hasShower: response.data.hasShower !== false,
        hasParking: response.data.hasParking !== false,
        images: response.data.images || [],
        amenities: response.data.amenities || []
      });
    } catch (error) {
      toast.error("Saha bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/venues/${venueId}/settings`, settings);
      toast.success("Ayarlar kaydedildi!");
      navigate("/venue-owner-dashboard");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="outline" 
            onClick={() => navigate("/venue-owner-dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Panele Dön
          </Button>

          <h1 className="text-3xl font-bold mb-6">Saha Ayarları</h1>

          <div className="space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Temel Bilgiler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Saha Adı</Label>
                    <Input
                      value={settings.name}
                      onChange={(e) => setSettings({...settings, name: e.target.value})}
                      placeholder="Örn: Merkez Halı Saha"
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={settings.phone}
                      onChange={(e) => setSettings({...settings, phone: e.target.value})}
                      placeholder="0555 123 45 67"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>İl</Label>
                    <Select 
                      value={settings.city}
                      onValueChange={(val) => setSettings({...settings, city: val})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="İl seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="İstanbul">İstanbul</SelectItem>
                        <SelectItem value="Ankara">Ankara</SelectItem>
                        <SelectItem value="İzmir">İzmir</SelectItem>
                        <SelectItem value="Bursa">Bursa</SelectItem>
                        <SelectItem value="Antalya">Antalya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>İlçe</Label>
                    <Input
                      value={settings.district}
                      onChange={(e) => setSettings({...settings, district: e.target.value})}
                      placeholder="İlçe"
                    />
                  </div>
                  <div>
                    <Label>Mahalle/Semt</Label>
                    <Input
                      value={settings.location}
                      onChange={(e) => setSettings({...settings, location: e.target.value})}
                      placeholder="Mahalle"
                    />
                  </div>
                </div>

                <div>
                  <Label>Adres</Label>
                  <Textarea
                    value={settings.address}
                    onChange={(e) => setSettings({...settings, address: e.target.value})}
                    placeholder="Tam adres..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Açıklama</Label>
                  <Textarea
                    value={settings.description}
                    onChange={(e) => setSettings({...settings, description: e.target.value})}
                    placeholder="Saha hakkında bilgi..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card>
              <CardHeader>
                <CardTitle>Teknik Özellikler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Format</Label>
                    <Select 
                      value={settings.format}
                      onValueChange={(val) => setSettings({...settings, format: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5x5">5 vs 5</SelectItem>
                        <SelectItem value="6x6">6 vs 6</SelectItem>
                        <SelectItem value="7x7">7 vs 7</SelectItem>
                        <SelectItem value="8x8">8 vs 8</SelectItem>
                        <SelectItem value="11x11">11 vs 11</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Zemin Tipi</Label>
                    <Select 
                      value={settings.surfaceType}
                      onValueChange={(val) => setSettings({...settings, surfaceType: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="artificial_grass">Suni Çim</SelectItem>
                        <SelectItem value="natural_grass">Doğal Çim</SelectItem>
                        <SelectItem value="synthetic">Sentetik</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Kapasite</Label>
                    <Input
                      type="number"
                      value={settings.capacity}
                      onChange={(e) => setSettings({...settings, capacity: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Çalışma Saatleri</Label>
                    <Input
                      value={settings.availableHours}
                      onChange={(e) => setSettings({...settings, availableHours: e.target.value})}
                      placeholder="08:00-23:00"
                    />
                  </div>
                  <div>
                    <Label>Saatlik Ücret (₺)</Label>
                    <Input
                      type="number"
                      value={settings.pricePerHour}
                      onChange={(e) => setSettings({...settings, pricePerHour: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Facilities */}
            <Card>
              <CardHeader>
                <CardTitle>Tesisler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Soyunma Odası</Label>
                  <Switch
                    checked={settings.hasChangingRoom}
                    onCheckedChange={(val) => setSettings({...settings, hasChangingRoom: val})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Duş</Label>
                  <Switch
                    checked={settings.hasShower}
                    onCheckedChange={(val) => setSettings({...settings, hasShower: val})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Otopark</Label>
                  <Switch
                    checked={settings.hasParking}
                    onCheckedChange={(val) => setSettings({...settings, hasParking: val})}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="w-full h-14 text-lg bg-gradient-to-r from-blue-500 to-blue-600"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VenueSettings;
